// Layout maths for the iReal Pro 16-cell grid, shared by the chart view and the
// mirrored diagram grid. Kept out of the component files so fast-refresh stays
// happy (component modules should only export components).

import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/state/store';
import { scaleFactor } from '@/state/displayScale';
import type { IRealChart, IRealMeasure } from '@/music/ireal/types';

export const CELLS_PER_ROW = 16;
/** Cells allotted to one bar (iReal packs four bars per 16-cell row). */
export const BAR_CELLS = 4;

export interface Placement {
  row: number;
  col: number;
  span: number;
  /** First measure on its row (gets a left barline). */
  rowStart: boolean;
  /** Last measure on its row (gets a closing barline on the right). */
  rowEnd: boolean;
}

/**
 * Lay measures out on iReal Pro's 16-cell grid: when the chart carries imported
 * cell positions we honour them exactly (so 4 bars fall per line and 2nd-time
 * bars align under 1st-time bars); otherwise we pack bars left-to-right.
 *
 * The grid is re-based at every section/rehearsal mark: like iReal Pro, a new
 * section begins a fresh line flush at the left margin, and cells are measured
 * relative to that section's start. This keeps endings aligned within a section
 * (via the imported padding) while preventing a wide bar's cell drift from
 * indenting later sections (e.g. a 2-chord-with-alternate bar in Stella).
 */
export function computeLayout(
  chart: IRealChart,
  cellsPerRow: number = CELLS_PER_ROW,
  slotChords = 2,
): Placement[] {
  const ms = chart.measures;
  const CPR = cellsPerRow; // bars per line = CPR / BAR_CELLS (16→4, 8→2 on phones)
  const hasGrid =
    ms.length > 0 && ms.every((m) => typeof m.cell === 'number' && typeof m.cells === 'number');

  const base: { row: number; col: number; span: number }[] = [];
  // `slotChords` is how many grips fit side by side in one bar's slot (see
  // diagramSlotChords). Below two, the authored grid would squeeze a
  // two-chord bar's grips, so the sheet flows by content at any density.
  if (CPR !== CELLS_PER_ROW || slotChords < 2) {
    // The phone diagram sheet (2 bars per line). The authored grid describes a
    // 16-cell page and cannot be halved by cell arithmetic: a condensed row
    // (Crystal Silence authors FIVE bars on one line) lands two bars on the
    // same column — the snap clamp tops out at CPR-BAR_CELLS, so the
    // left-to-right guard cannot push past it and the diagrams draw on top of
    // each other — while the dense-row fallback pinches a 4-chord bar into a
    // quarter of the line (unreadable thumbnails). Ignore authored cells here
    // and flow by CONTENT: a bar is one slot, but a busy bar (3+ chords)
    // takes the whole line so each diagram keeps a readable width (on the
    // Arrange sheets, any bar with more grips than fit in its slot). Volta
    // column alignment is a 4-bars-per-line concept; at 2 bars per line the
    // sheet reads linearly anyway.
    let row = 0;
    let col = 0;
    for (const m of ms) {
      const span = m.chords.length > slotChords ? CPR : BAR_CELLS;
      if (col + span > CPR) {
        row += 1;
        col = 0;
      }
      base.push({ row, col, span });
      col += span;
      if (col >= CPR) {
        row += 1;
        col = 0;
      }
    }
  } else if (hasGrid) {
    // iReal lays four four-cell bars per 16-cell row. We honour the imported
    // grid for row breaks and ending alignment, but a single bar can carry extra
    // cells (e.g. two chords plus an alternate). Snap each bar to the four-cell
    // bar grid so one wide bar can't knock the rest of its row — or later rows —
    // out of the 0/4/8/12 columns; spans then tile up to the next bar.
    let sectionBaseCell = 0;
    let sectionBaseRow = 0;
    let prevRow = 0;
    const rawCols: number[] = []; // authored (un-snapped) cell offset within the row
    ms.forEach((m, idx) => {
      // A section mark after the first bar starts a new line, re-based here.
      if (idx > 0 && m.section != null) {
        sectionBaseCell = m.cell!;
        sectionBaseRow = prevRow + 1;
      }
      const rel = m.cell! - sectionBaseCell;
      let row = sectionBaseRow + Math.floor(rel / CPR);
      let rawCol = ((rel % CPR) + CPR) % CPR;
      let col = Math.min(Math.round(rawCol / BAR_CELLS) * BAR_CELLS, CPR - BAR_CELLS);
      // A wide bar (more than one bar-slot) that would straddle the end of its
      // row starts fresh on the next line, the way iReal lays out a tag/coda —
      // otherwise it leaves a ragged half-empty slot on both rows (e.g. the
      // 12-cell C-7 tag in 500 Miles High landing mid-row at col 8).
      const cells = Math.max(1, m.cells!);
      if (
        col > 0 &&
        cells > BAR_CELLS &&
        Math.floor((rel + cells - 1) / CPR) > Math.floor(rel / CPR)
      ) {
        row += 1;
        col = 0;
        rawCol = 0;
      }
      // Keep bars on the same row strictly left-to-right.
      const prev = base[idx - 1];
      if (idx > 0 && prev.row === row && col <= prev.col) {
        col = Math.min(prev.col + BAR_CELLS, CPR - BAR_CELLS);
      }
      base.push({ row, col, span: Math.max(1, m.cells!) });
      rawCols.push(rawCol);
      prevRow = row;
    });
    // Dense rows: a condensed chart can author MORE bars per line than there are
    // four-cell slots (A Felicidade packs eight 2-cell bars on one row). Snapping
    // would pile the overflow onto the last slot, stacking the bars on top of
    // each other — lay those rows out at their authored cells instead.
    const byRow = new Map<number, number[]>();
    base.forEach((p, i) => {
      const list = byRow.get(p.row) ?? [];
      list.push(i);
      byRow.set(p.row, list);
    });
    const barsPerRow = CPR / BAR_CELLS;
    byRow.forEach((idxs) => {
      if (idxs.length <= barsPerRow) return;
      let prevCol = -1;
      for (const i of idxs) {
        const col = Math.min(Math.max(rawCols[i], prevCol + 1), CPR - 1);
        base[i].col = col;
        prevCol = col;
      }
    });
    // De-collision + legibility re-flow. The snap-to-4 grid and the left-to-right
    // clamp above can seat two bars on the SAME column at a row's end — a wide
    // bar earlier in the row pushes the rest past the last four-cell slot, where
    // they all clamp to CPR-BAR_CELLS and overprint (e.g. Budo's turnaround
    // bars) — and can pinch a busy bar into a single cell, crushing its symbols
    // and diagrams together.
    const wantRow = base.map((p) => p.row);
    const wantCol = base.map((p) => p.col);
    // Each bar's INTENDED display width: fill to the next bar on the same
    // authored row (capped by its own cell count), i.e. one four-cell slot for
    // an ordinary bar and several for a genuine wide bar. Computed from the
    // authored positions so a clean chart's slots are reproduced exactly.
    const footprint = base.map((p, idx) => {
      const next = base[idx + 1];
      const limit = next && wantRow[idx + 1] === wantRow[idx] ? wantCol[idx + 1] : CPR;
      return Math.max(1, Math.min(p.span, limit - p.col));
    });
    // Walk the bars once with a per-row cursor: keep each bar's authored row and
    // column while they still fit after the previous bar; when a bar would land
    // on top of its neighbour or run off the line, wrap it (and everything after
    // it) down to a fresh row. Absolute row numbers are preserved (only extra
    // rows from a wrap are added), so a chart that already lays out cleanly is
    // reproduced exactly and only overlapping rows are repaired.
    let rowShift = 0;
    let curRow: number | null = null;
    let cursor = 0;
    base.forEach((p, idx) => {
      let row = wantRow[idx] + rowShift;
      if (curRow === null || row !== curRow) {
        curRow = row;
        cursor = 0;
      }
      let col = Math.max(wantCol[idx], cursor);
      if (col > 0 && col + footprint[idx] > CPR) {
        rowShift += 1; // no room left on this line — drop to a fresh one
        row = wantRow[idx] + rowShift;
        curRow = row;
        col = 0;
        cursor = 0;
      }
      p.row = curRow;
      p.col = Math.min(col, CPR - 1);
      cursor = p.col + footprint[idx];
    });
    // Span: fill the free cells up to the next bar on the (final) row, widening a
    // pinched bar toward ~2 cells per chord so a busy bar's symbols and diagrams
    // stay legible. It only ever grows into space that is actually free before
    // the next bar, so bars never overlap and a packed row is left as authored.
    base.forEach((p, idx) => {
      const next = base[idx + 1];
      const avail = (next && next.row === p.row ? next.col : CPR) - p.col;
      const chords = ms[idx].chords.length || 1;
      const need = Math.min(chords * 2, CPR);
      p.span = Math.max(1, Math.min(Math.max(footprint[idx], need), avail));
    });
  } else {
    let row = 0;
    let col = 0;
    let meter = chart.timeSignature;
    for (const m of ms) {
      meter = m.timeSig ?? meter;
      const span = Math.min(Math.max(1, m.cells ?? meter[0]), CPR);
      if (col + span > CPR) {
        row += 1;
        col = 0;
      }
      base.push({ row, col, span });
      col += span;
      if (col >= CPR) {
        row += 1;
        col = 0;
      }
    }
  }

  // Mark the first and last measure of each row so the lead-sheet renderer can
  // draw a left barline at the start and a closing barline at the end of a line.
  return base.map((p, i) => ({
    ...p,
    rowStart: p.col === 0 || i === 0 || base[i - 1].row !== p.row,
    rowEnd: i === base.length - 1 || base[i + 1].row !== p.row,
  }));
}

/** Structural (barline + row-edge) CSS classes for a measure, shared by views. */
export function structuralClasses(m: IRealMeasure, place: Placement): string {
  return [
    m.open ? `open-${m.open}` : '',
    m.close ? `close-${m.close}` : '',
    place.rowStart ? 'row-start' : '',
    place.rowEnd ? 'row-end' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** How many LINES the sheet lays out. The printed sheet draws one block per
 * line so a page break can fall between them, and the count is the block
 * count — pure, from the same layout the grid draws. */
export function chartRowCount(
  chart: IRealChart,
  cellsPerRow: number = CELLS_PER_ROW,
  slotChords = 2,
): number {
  const layout = computeLayout(chart, cellsPerRow, slotChords);
  return layout.length ? Math.max(...layout.map((place) => place.row)) + 1 : 0;
}

/**
 * A fretboard diagram needs roughly two grid cells to read cleanly, but the
 * tightest bar in a chart — a busy turnaround, or a densely-authored row — can
 * fall well below that at iReal's 4-bars-per-line, crushing its diagrams (a
 * 3-chord ending bar can pinch each diagram to single digits of pixels). Return
 * the viewport width below which that tightest bar would crush; under it the
 * sheet drops to 2 bars/line (8 cells), which flows a busy bar onto its own line
 * at a readable size. A chart with room to spare keeps 4-bars-per-line down to
 * the phone width, so ordinary charts are unaffected.
 *
 * "Legible" is RELATIVE to the user's Chord size: at 200% a grip needs twice
 * the room, so the breakpoint scales with it and the sheet trades bars per
 * line for diagram size, the way iReal does. Without this the slider grew the
 * symbols while the bar cell pinned the grips (lives here, beside the grid it measures).
 */
export function halfWidthBreakpoint(chart: IRealChart, scale = 1): number {
  // Nothing tighter than ~2 cells/chord: at 100% only phones need the
  // half-width sheet; a larger Chord size raises the bar proportionally.
  // Below 100% the floor FOLLOWS the scale DOWN: the user chose smaller
  // grips, so a narrow window should reflow denser (useChartCells) instead
  // of being forced onto the 2-bars-per-line phone sheet with the symbols
  // shrunk anyway — that combination was tiny AND sparse at once. 360 keeps
  // a true phone portrait on the half-width sheet at any scale.
  const floor = Math.min(1400, Math.max(360, Math.round(700 * scale)));
  // The tight-chart elevation protects grips inside the AUTHORED grid, where a
  // 4-chord bar is pinned to its 4 cells. Zoomed out (<100%) the content-flow
  // layout takes over and a busy bar spans a whole line (computeLayout's
  // CPR≠16 branch), so it protects itself — elevating the breakpoint there
  // just forced the phone sheet's 2 bars per line onto windows that wanted
  // density.
  const tight = scale < 1 ? 0 : tightGridWidth(chart, scale);
  if (!tight) return floor;
  return Math.min(1400, Math.max(floor, tight));
}

/** The width below which this chart's TIGHTEST bar stops rendering legibly on
 * the authored four-bars-to-a-line grid, or 0 where no bar is that tight.
 *
 * A diagram renders at about cellsPerChord·(width − gutter)/16 − padding;
 * solve for where the tightest bar dips under a legible size. This is the
 * legibility half of `halfWidthBreakpoint` WITHOUT its viewport floor, which
 * is a statement about phones ("a window this narrow is a phone") and not
 * about paper: a printed page is 673px wide and is not a phone, so the
 * printed sheet asks this instead and keeps the four-bar page unless the
 * music itself cannot fit on it. */
export function tightGridWidth(chart: IRealChart, scale = 1): number {
  const layout = computeLayout(chart, CELLS_PER_ROW); // the 4-bars-per-line grid
  let minCellsPerChord = Infinity;
  chart.measures.forEach((m, i) => {
    const n = m.chords.length;
    if (n > 0) minCellsPerChord = Math.min(minCellsPerChord, layout[i].span / n);
  });
  if (!Number.isFinite(minCellsPerChord) || minCellsPerChord >= 2) return 0;
  const GUTTER = 32;
  const PADDING = 24;
  const LEGIBLE = 46 * scale;
  return Math.min(
    1400,
    Math.round(((LEGIBLE + PADDING) * CELLS_PER_ROW) / minCellsPerChord + GUTTER),
  );
}

/** Bars per line on PAPER: the authored four-bar page, dropping to two only
 * where the tightest bar would print its chord symbols on top of each other
 * (measured on Body And Soul, whose busiest bar carries three chords). */
export function chartCellsForPaper(chart: IRealChart, scale: number, width: number): number {
  return width <= tightGridWidth(chart, scale) ? 8 : CELLS_PER_ROW;
}

/** The live viewport width. Only ever setState on a real resize event (mount
 * effect, empty deps), so it can never drive a render→setState→render loop. */
function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1280,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const on = () => setWidth(window.innerWidth);
    on();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return width;
}

/**
 * Cells per row for BOTH chart-surface sheets (the symbol chart and the
 * diagram sheet), from one rule:
 *
 *  - phones (below the legibility breakpoint): 8 cells — two bars per line,
 *    the existing half-width sheet;
 *  - at 100% Chord size and up: the authored iReal 16-cell grid, exactly;
 *  - zoomed OUT below 100%: the page reflows like the score — smaller
 *    content means MORE bars per line, not the same four bars in a sea of
 *    paper. Bars-per-line comes from the width a bar needs at this scale
 *    (~200px at 100%), clamped to 4..8; the authored grid cannot express
 *    these densities, so layout flows by content (the same trade the phone
 *    sheet already makes: voltas read linearly).
 */
export function useChartCells(chart: IRealChart, scaleOverride?: number): number {
  const chartScale = useStore((s) => s.chartScale);
  const scale = scaleFactor(scaleOverride ?? chartScale);
  const viewport = useViewportWidth();
  return useMemo(
    () =>
      chartCellsForWidth(
        chart,
        scale,
        viewport,
        scaleOverride === undefined ? undefined : diagramBarWidth(scale),
      ),
    [chart, scale, viewport, scaleOverride],
  );
}

/** One Arrange grip's width: ten staff spaces on the score's device base, so
 * chart reflow makes room for the chosen diagram size. */
export function diagramGripWidth(scale: number): number {
  const base =
    typeof window === 'undefined'
      ? 10.5
      : Number.parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue('--score-space-base'),
        ) || 10.5;
  return base * 10 * scale;
}

/** The narrowest bar an Arrange sheet lays out: one full-size grip plus the
 * bar's gutters. Bars per line come from this; a bar holding more grips than
 * its slot fits takes a whole line instead (diagramSlotChords). It used to
 * budget TWO grips for every bar, which put one bar on each line of a 390px
 * phone at the default size, single-chord bars included. */
export function diagramBarWidth(scale: number): number {
  return diagramGripWidth(scale) + 24;
}

/** How many grips fit side by side in one bar's slot at this width and
 * density: 2 when a two-chord bar keeps full-size grips, else 1. */
export function diagramSlotChords(width: number, cellsPerRow: number, scale: number): number {
  const bars = Math.max(1, cellsPerRow / BAR_CELLS);
  const slot = (width - 64) / bars;
  return slot >= diagramGripWidth(scale) * 2 + 24 ? 2 : 1;
}

/** diagramSlotChords at the viewport's width; 2 (the plain chart's rule)
 * when no Arrange scale is given. */
export function useDiagramSlotChords(cellsPerRow: number, scaleOverride?: number): number {
  const viewport = useViewportWidth();
  return useMemo(
    () =>
      scaleOverride === undefined
        ? 2
        : diagramSlotChords(viewport, cellsPerRow, scaleFactor(scaleOverride)),
    [viewport, cellsPerRow, scaleOverride],
  );
}

/** The same rule for an arbitrary width: page view sizes cells by the PAGE the
 * sheet actually spans, not the viewport it happens to sit in. */
export function chartCellsForWidth(
  chart: IRealChart,
  scale: number,
  width: number,
  minBarWidth?: number,
): number {
  let cells = CELLS_PER_ROW;
  if (width <= halfWidthBreakpoint(chart, scale)) cells = 8;
  else if (scale < 1) {
    const bars = Math.min(8, Math.max(4, Math.floor((width - 64) / (200 * scale + 24))));
    cells = bars * BAR_CELLS;
  }
  // Arrange can reflow down to one bar per line at large sizes on phones.
  // Otherwise its half-bar slots silently cap the slider's effect. Keep
  // the chart's existing tighter limit for busy authored bars as well.
  // A bar here is ONE grip wide; bars with more take a whole line.
  if (minBarWidth !== undefined) {
    const bars = Math.floor((width - 64) / minBarWidth);
    cells = Math.min(cells, (bars >= 4 ? 4 : bars >= 2 ? 2 : 1) * BAR_CELLS);
  }
  return cells;
}
